/**
 * API origin resolution — PURE module, no side effects.
 *
 * `utils/apiUtils.ts` historically owned `getApiBaseUrl`, but that module also
 * imports `config/firebase` and `services/analyticsService`, both of which run
 * side effects at import time (Firebase app init, analytics session bootstrap).
 * Anything that only needs to know "where is the backend" — URL builders,
 * presentational primitives, the storage-URL policy — must not drag those in
 * (GCS URL exposure Phase 4, DL-34). So the resolution lives here, with zero
 * imports, and `apiUtils.ts` re-exports it so its 160+ existing importers keep
 * working unchanged.
 *
 * Resolution order is unchanged from the original implementation:
 *   1. `VITE_API_URL` when set (production sets `https://api.arterra.ai`,
 *      staging its Cloud Run host, local dev `http://localhost:4000`);
 *   2. same-origin (`''`) in a production build with no explicit URL;
 *   3. `https://localhost:4000` otherwise.
 */
export const getApiBaseUrl = (): string => {
  const envUrl = import.meta.env.VITE_API_URL as string | undefined;
  if (envUrl && envUrl.trim().length > 0) return envUrl;
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'production') {
    // Default to same-origin in production when env not set
    return '';
  }
  // SECURITY FIX: Always use HTTPS, even in development
  // Developers should configure proper certificates or use a proxy
  return 'https://localhost:4000';
};

/** Router-local path of the anonymous static-image proxy (`staticimages.ts`). */
export const STATIC_IMAGE_PROXY_PATH = '/api/static/image';

/**
 * Base of the static-image proxy for THIS environment:
 * `<api origin>/api/static/image`. Every proxied image URL the frontend builds
 * is `${getStaticImageProxyBase()}/${encodeURIComponent(objectPath)}`.
 */
export const getStaticImageProxyBase = (): string => `${getApiBaseUrl()}${STATIC_IMAGE_PROXY_PATH}`;

/**
 * Optional SECONDARY static-image proxy origin, used by the marketing
 * renderers as an error fallback (a developer pointed at a backend that does
 * not serve the bucket). Configured per environment via
 * `VITE_STATIC_IMAGE_FALLBACK_API_URL`; when unset there is no fallback origin
 * and the renderers retry against the configured proxy only.
 *
 * Replaces the hardcoded production host that previously lived in
 * `marketing/seo.ts`, `marketing/ResponsivePicture.tsx` and
 * `home/marketplace/MarketplaceHome.tsx` (GCS URL exposure Phase 4, P4.8): a
 * literal prod origin forced cross-origin production calls from every
 * environment and silently coupled staging/dev to prod's bucket.
 */
export const getStaticImageFallbackProxyBase = (): string | null => {
  const fallbackOrigin = import.meta.env.VITE_STATIC_IMAGE_FALLBACK_API_URL as string | undefined;
  if (!fallbackOrigin || fallbackOrigin.trim().length === 0) return null;
  const trimmed = fallbackOrigin.trim().replace(/\/+$/, '');
  if (trimmed === getApiBaseUrl().replace(/\/+$/, '')) return null;
  return `${trimmed}${STATIC_IMAGE_PROXY_PATH}`;
};
